import { useEffect, useRef, useState } from 'react'
import { Button } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import { Video, VideoOff, X } from 'lucide-react'

// Livebild einer Drohne (DJI Cloud API Livestream): DJI Pilot 2 sendet per RTMP an MediaMTX,
// der Browser zeigt bevorzugt WebRTC (WHEP, ca. 0,5–1 s Verzögerung, Signalisierung über /live-rtc/<pfad>/whep, Medien direkt über
// den öffentlichen Medien-Port) und fällt bei Fehler/Timeout auf den HLS-Player von MediaMTX unter /live/<pfad>/ zurück (2–4 s).

export interface LivebildInfo {
  laeuft: boolean
  url: string | null
  anforderbar: boolean
  /** Livestream-Freigabe der Fernsteuerung: laeuft | bereit | unbekannt | nicht_freigegeben */
  freigabe?: { stufe: 'laeuft' | 'bereit' | 'unbekannt' | 'nicht_freigegeben'; text: string }
  dji_meldet_stream: boolean
  meldung: string | null
  /** Aktive Linse des Stroms: normal / wide / zoom / ir */
  linse?: string | null
  /** Ströme, die die Fernsteuerung gleichzeitig übertragen kann (RC: 1) */
  gleichzeitig_max?: number | null
  kameras?: LivebildKamera[]
}

export interface LivebildKamera {
  video_id: string
  camera_index: string
  /** false = Kamera der Fernsteuerung selbst */
  drohne: boolean
  linse: string | null
  linsen: string[]
  aktiv: boolean
}

/** Knöpfe und Status in der Drohnen-Seitenleiste. steuerbar = Anfordern/Beenden per Cloud (Führung). */
export function LivebildLeiste({ sn, livebild, steuerbar, onAnsehen, onGeaendert }: {
  sn: string
  livebild: LivebildInfo | undefined
  steuerbar: boolean
  onAnsehen: () => void
  onGeaendert?: () => void
}) {
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  if (!livebild) return null

  async function steuern(aktion: 'start' | 'stop') {
    setLaeuft(true); setFehler(null)
    try {
      await api.post(`/dji/live/${encodeURIComponent(sn)}/livebild/${aktion}`)
      onGeaendert?.()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Livebild konnte nicht gesteuert werden')
    } finally { setLaeuft(false) }
  }

  return (
    <div className="mt-2 space-y-1">
      <div className="flex gap-1">
        {livebild.url ? (
          <Button size="sm" className="min-h-[48px] flex-1" onClick={onAnsehen}><Video className="mr-1 h-4 w-4" />Livebild</Button>
        ) : (
          <div className="flex flex-1 items-center gap-1 text-[11px] text-dark-text-subtle"><VideoOff className="h-3.5 w-3.5" />kein Livebild</div>
        )}
        {steuerbar && !livebild.laeuft && livebild.anforderbar && (
          <Button size="sm" variant="secondary" className="min-h-[48px]" disabled={laeuft} onClick={() => steuern('start')}>Anfordern</Button>
        )}
        {steuerbar && (livebild.laeuft || livebild.dji_meldet_stream) && (
          <Button size="sm" variant="secondary" className="min-h-[48px]" disabled={laeuft} onClick={() => steuern('stop')}>Beenden</Button>
        )}
      </div>
      {livebild.freigabe && <FreigabeAnzeige freigabe={livebild.freigabe} />}
      {(fehler || livebild.meldung) && (
        <div className={`text-[11px] ${fehler ? 'text-red-300' : 'text-dark-text-subtle'}`}>{fehler || livebild.meldung}</div>
      )}
    </div>
  )
}

export interface UebertragungsInfo {
  weg: 'webrtc' | 'hls'
  /** geschätzte Bildverzögerung in ms (WebRTC: Jitter-Puffer + RTT/2; HLS: Abstand zum Live-Rand); null = unbekannt */
  verzoegerung_ms: number | null
}

const WEBRTC_TIMEOUT_MS = 5000

/** Wartet (höchstens ms) bis das Sammeln der ICE-Kandidaten fertig ist (WHEP ohne Trickle-ICE). */
function iceFertig(pc: RTCPeerConnection, ms: number): Promise<void> {
  return new Promise(resolve => {
    if (pc.iceGatheringState === 'complete') return resolve()
    const ende = () => { clearTimeout(t); pc.removeEventListener('icegatheringstatechange', pruefen); resolve() }
    const pruefen = () => { if (pc.iceGatheringState === 'complete') ende() }
    const t = setTimeout(ende, ms)
    pc.addEventListener('icegatheringstatechange', pruefen)
  })
}

/**
 * Livebild-Player: erst WebRTC (WHEP), bei Fehler/Timeout (5 s ohne Bild) oder Abbruch Rückfall auf den HLS-Player von MediaMTX.
 * url = bisherige HLS-Adresse (/live/<pfad>/), der Pfad wird daraus abgeleitet. nurHls erzwingt HLS (z. B. Test).
 */
export function LivebildPlayer({ url, titel, className, pointerEventsNone, onUebertragung, nurHls }: {
  url: string
  titel: string
  className?: string
  pointerEventsNone?: boolean
  onUebertragung?: (info: UebertragungsInfo) => void
  nurHls?: boolean
}) {
  const pfad = /\/live\/(d-[0-9a-f]{24})/.exec(url)?.[1] ?? null
  const [weg, setWeg] = useState<'webrtc' | 'hls'>(nurHls || !pfad || typeof RTCPeerConnection === 'undefined' ? 'hls' : 'webrtc')
  const videoRef = useRef<HTMLVideoElement>(null)
  const frameRef = useRef<HTMLIFrameElement>(null)
  const cbRef = useRef(onUebertragung)
  cbRef.current = onUebertragung

  // WebRTC
  useEffect(() => {
    if (weg !== 'webrtc' || !pfad) return
    let beendet = false
    let sitzung: string | null = null
    const pc = new RTCPeerConnection({ iceServers: [] })
    const abbruch = new AbortController()
    const zurueck = () => { if (!beendet) { beendet = true; setWeg('hls') } }
    pc.addTransceiver('video', { direction: 'recvonly' })
    pc.ontrack = e => {
      const v = videoRef.current
      if (v && e.streams[0]) { v.srcObject = e.streams[0]; v.play().catch(() => {}) }
    }
    pc.onconnectionstatechange = () => { if (pc.connectionState === 'failed') zurueck() }

    const start = Date.now()
    let letzt = { verz: 0, anz: 0 }
    let hatBild = false
    let getrennt = 0
    const statsTimer = setInterval(async () => {
      if (beendet) return
      if (pc.connectionState === 'disconnected') { if (++getrennt >= 3) return zurueck() } else getrennt = 0
      try {
        let frames = 0, verz = 0, anz = 0, rtt: number | null = null
        const stats = await pc.getStats()
        stats.forEach((r: any) => {
          if (r.type === 'inbound-rtp' && r.kind === 'video') { frames = r.framesDecoded || 0; verz = r.jitterBufferDelay || 0; anz = r.jitterBufferEmittedCount || 0 }
          if (r.type === 'candidate-pair' && (r.state === 'succeeded' && (r.nominated || r.selected)) && typeof r.currentRoundTripTime === 'number') rtt = r.currentRoundTripTime
        })
        if (frames > 0) hatBild = true
        else if (!hatBild && Date.now() - start > WEBRTC_TIMEOUT_MS) return zurueck()
        if (hatBild) {
          // Verzögerung der letzten Sekunden (Mittel der ausgegebenen Bilder), sonst Gesamtmittel
          const dAnz = anz - letzt.anz, dVerz = verz - letzt.verz
          const jb = dAnz > 0 ? (dVerz / dAnz) * 1000 : anz > 0 ? (verz / anz) * 1000 : null
          letzt = { verz, anz }
          cbRef.current?.({ weg: 'webrtc', verzoegerung_ms: jb === null ? null : Math.round(jb + (rtt ?? 0) * 500) })
        }
      } catch { /* nächster Versuch */ }
    }, 1000)

    ;(async () => {
      try {
        const angebot = await pc.createOffer()
        await pc.setLocalDescription(angebot)
        await iceFertig(pc, 2000)
        const r = await fetch(`/live-rtc/${pfad}/whep`, {
          method: 'POST', headers: { 'Content-Type': 'application/sdp' }, body: pc.localDescription!.sdp, signal: abbruch.signal,
        })
        if (r.status !== 201) throw new Error(`WHEP ${r.status}`)
        const loc = r.headers.get('Location')
        if (loc) sitzung = loc.startsWith('/live-rtc/') ? loc : `/live-rtc${loc.startsWith('/') ? '' : `/${pfad}/`}${loc}`
        await pc.setRemoteDescription({ type: 'answer', sdp: await r.text() })
      } catch { zurueck() }
    })()

    return () => {
      beendet = true
      clearInterval(statsTimer)
      abbruch.abort()
      if (sitzung) fetch(sitzung, { method: 'DELETE', keepalive: true }).catch(() => {})
      pc.close()
      if (videoRef.current) videoRef.current.srcObject = null
    }
  }, [weg, pfad, url])

  // HLS: Abstand zum Live-Rand aus dem Video im (gleichen Ursprungs) Player-Frame
  useEffect(() => {
    if (weg !== 'hls') return
    const t = setInterval(() => {
      let ms: number | null = null
      try {
        const v = frameRef.current?.contentDocument?.querySelector('video')
        if (v && v.buffered.length > 0 && !v.paused) ms = Math.max(0, Math.round((v.buffered.end(v.buffered.length - 1) - v.currentTime) * 1000))
      } catch { /* Frame nicht lesbar */ }
      cbRef.current?.({ weg: 'hls', verzoegerung_ms: ms })
    }, 2000)
    cbRef.current?.({ weg: 'hls', verzoegerung_ms: null })
    return () => clearInterval(t)
  }, [weg, url])

  if (weg === 'webrtc') {
    return <video ref={videoRef} autoPlay muted playsInline title={`Livebild ${titel}`}
      className={`${className ?? 'absolute inset-0 h-full w-full'} bg-black object-contain ${pointerEventsNone ? 'pointer-events-none' : ''}`} />
  }
  return <iframe ref={frameRef} src={url} title={`Livebild ${titel}`} allow="autoplay; fullscreen" allowFullScreen
    className={`${className ?? 'absolute inset-0 h-full w-full'} ${pointerEventsNone ? 'pointer-events-none' : ''}`} />
}

/** Großes Fenster mit dem Livebild (WebRTC mit Rückfall auf den HLS-Player von MediaMTX, lädt bei Unterbrechung selbst neu). */
export function LivebildFenster({ titel, url, onSchliessen }: { titel: string; url: string | null; onSchliessen: () => void }) {
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 p-4" onClick={onSchliessen}>
      <div className="w-full max-w-5xl overflow-hidden rounded-xl border border-dark-border bg-dark-bg-secondary" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-dark-border px-4 py-2">
          <span className="text-sm font-semibold text-dark-text">Livebild · {titel}</span>
          <button type="button" className="text-dark-text-muted hover:text-dark-text" onClick={onSchliessen} aria-label="Schließen"><X className="h-5 w-5" /></button>
        </div>
        <div className="relative aspect-video w-full bg-black">
          {url ? (
            <LivebildPlayer url={url} titel={titel} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-dark-text-muted">Kein Bild – die Drohne sendet derzeit nicht.</div>
          )}
        </div>
      </div>
    </div>
  )
}


const FREIGABE_STIL: Record<string, string> = {
  laeuft: 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200',
  bereit: 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200',
  unbekannt: 'border-amber-500/60 bg-amber-500/10 text-amber-200',
  nicht_freigegeben: 'border-red-500/60 bg-red-500/10 text-red-200',
}

/** Zustand der Livestream-Freigabe (aus den Meldungen der Fernsteuerung, nicht aus einem erfolgreichen Aufruf). */
export function FreigabeAnzeige({ freigabe }: { freigabe: NonNullable<LivebildInfo['freigabe']> }) {
  return (
    <div className={`rounded border px-2 py-1 text-[11px] font-medium ${FREIGABE_STIL[freigabe.stufe] || FREIGABE_STIL.unbekannt}`} data-testid="livestream-freigabe" data-stufe={freigabe.stufe}>
      {freigabe.text}
    </div>
  )
}
